import type { JobError, VideoDetail } from '@/shared/types';
import { fetchLibraryItem, removeLibraryItem, updateLibraryItemStatus, upsertLibraryItem } from '@/features/library/services/libraryService';
import { describeFromText, mergeMetadata, sameMetadata, truncate } from '@/features/library/services/libraryApi';
import { fetchVideoStatus } from '@/features/content/services/videoApi';
import { listSavedContent } from '@/features/browse/services/savedContentService';
import { ApiError } from '@/shared/api/apiClient';

export type VideoDetailResult = {
  detail: VideoDetail;
  // True when `detail`'s status/URLs reflect a successful *fresh* backend read,
  // false when a status refresh threw and we fell back to the stale cache entry.
  // VideoPage uses this to avoid treating a cached completed-but-captionless entry
  // as an authoritative "no captions" answer when the refresh never actually ran.
  statusFresh: boolean;
};

export async function fetchVideoDetail(userId: string, id: string): Promise<VideoDetailResult | undefined> {
  // Structured failure detail (only present on a failed job) isn't cached in the
  // library — it's read fresh from whichever status fetch below runs.
  let error: JobError | null | undefined;
  let libraryEntry = fetchLibraryItem(userId, id);
  // Tracks whether we already pulled a fresh status below, so the cached-entry
  // refresh doesn't fire a redundant second request for a just-fetched video.
  let refreshedFromApi = false;
  // The awaiting_confirmation quote. Declared out here because either status
  // read can be the one that carries it, and neither is cached.
  let estimatedCost: number | undefined;
  let estimatedDurationSeconds: number | undefined;
  if (!libraryEntry) {
    /* A video saved from browse is a curated job the account does not own. Its
       id is the curation row's, and even the real job_id would 404 here — the
       /videos/{id} endpoints check ownership (engine text_api, get_for_user).
       So there is nothing to fetch: the saved record, written from the
       featured feed and refreshed on every browse visit, is the entire truth.
       This card used to fall through to the backend fetch below, 404, and
       render "Video not found" over a video that plays fine on /home.
       statusFresh: true for the same reason — a vtt-reconcile pass against a
       job we cannot read would only 404 twice more. */
    const saved = (await listSavedContent(userId)).find((item) => item.id === id);
    if (saved) {
      return {
        detail: {
          id: saved.id,
          title: saved.title,
          description: saved.description ?? '',
          videoUrl: saved.videoUrl,
          vttUrl: saved.vttUrl,
          status: 'completed',
          createdAt: saved.savedAt,
        },
        statusFresh: true,
      };
    }
    // If not found in cache, fetch status directly from backend
    try {
      const latest = await fetchVideoStatus(id);
      error = latest.error ?? error;
      refreshedFromApi = true;
      const title = latest.sourceText ? truncate(latest.sourceText, 40) : 'Video Detail';

      libraryEntry = upsertLibraryItem(userId, {
        id,
        title,
        description: describeFromText(latest.sourceText),
        status: latest.status,
        videoUrl: latest.videoUrl,
        sourceText: latest.sourceText,
        createdAt: new Date().toISOString(),
        metadata: latest.metadata,
        thumbnailUrl: latest.thumbnailUrl,
        vttUrl: latest.vttUrl,
      });
      // The quote comes with this read; it is not cached in the library, so it
      // has to be carried out of the branch rather than re-fetched below.
      estimatedCost = latest.estimatedCost;
      estimatedDurationSeconds = latest.estimatedDurationSeconds;
    } catch (err) {
      /* A 404 means the job is gone server-side — deleted here or in another
         session — so the cached row goes with it. Returning `undefined` without
         evicting would leave the library drawing a card for a video that no
         longer exists, and the next visit would 404 again. */
      if (err instanceof ApiError && err.status === 404) {
        removeLibraryItem(userId, id);
        return undefined;
      }
      /* Anything else keeps the pre-existing behaviour: fall back to the cached
         entry rather than failing the page. A flaky status read is not a reason
         to refuse to show a video we already have. */
      return undefined;
    }
  }

  let status = libraryEntry.status;
  let videoUrl = libraryEntry.videoUrl;
  let sourceText = libraryEntry.sourceText;
  let metadata = libraryEntry.metadata;
  let thumbnailUrl = libraryEntry.thumbnailUrl;
  let vttUrl = libraryEntry.vttUrl;
  // Whether the values above reflect a fresh backend read. Starts true only when
  // we already fetched fresh above; the refresh block below sets it on success and
  // clears it if the refresh throws and we keep the stale cache values.
  let statusFresh = refreshedFromApi;

  // Always refresh a cached entry from the backend: the presigned videoUrl,
  // thumbnailUrl and vttUrl expire (~15 min via their `Expires` param), so a
  // completed video served straight from cache can carry a dead playback URL and
  // a missing/stale caption track. Skipped only when we just fetched above — but
  // still refresh in that case if the fetch came back completed without a videoUrl
  // (CDN propagation lag) or without a vttUrl (caption still being written), so we
  // retry and pick up whichever URL lands moments later instead of treating its
  // absence as final.
  //
  // Cost note: for a caption-less job (cinematic — no vtt_s3_key per the captions
  // contract) the `!vttUrl` arm adds one extra fetch on the *first cold-cache
  // view only*; every repeat view instead hits the unconditional cached-entry
  // refresh above and fetches exactly once. We accept that bounded first-view
  // round-trip so a storytelling caption written just after the encode completes
  // still gets a second look here rather than being locked out for the session.
  if (status !== 'cancelled' && (!refreshedFromApi || !videoUrl || !vttUrl)) {
    try {
      const latest = await fetchVideoStatus(id);
      error = latest.error ?? error;
      statusFresh = true;
      status = latest.status;
      videoUrl = latest.videoUrl ?? videoUrl;
      sourceText = latest.sourceText ?? sourceText;
      thumbnailUrl = latest.thumbnailUrl ?? thumbnailUrl;
      vttUrl = latest.vttUrl ?? vttUrl;
      // `??`, like every other field here: this is the second read on the cold
      // path, and it must not blank a quote the first one already carried.
      estimatedCost = latest.estimatedCost ?? estimatedCost;
      estimatedDurationSeconds = latest.estimatedDurationSeconds ?? estimatedDurationSeconds;
      // Field-merge so the minimal status response can't wipe the rich cached
      // metadata (model, session, etc.) captured at submit time.
      metadata = mergeMetadata(metadata, latest.metadata);
      /* Compare the values we are about to persist against the ones already on
         the entry — not `latest.*` against the entry, which is what this used to
         do and which made the guard useless. `latest.metadata` is a fresh object
         literal built by fetchVideoStatus on every call; `libraryEntry.metadata`
         came out of JSON.parse. The two are never reference-equal, so the `||`
         chain was true whenever metadata existed at all, and every detail view
         re-read, re-parsed, re-mapped and re-stringified the whole library array
         to write back bytes identical to what was there. */
      if (
        status !== libraryEntry.status ||
        videoUrl !== libraryEntry.videoUrl ||
        sourceText !== libraryEntry.sourceText ||
        thumbnailUrl !== libraryEntry.thumbnailUrl ||
        vttUrl !== libraryEntry.vttUrl ||
        !sameMetadata(metadata, libraryEntry.metadata)
      ) {
        // Persist the merged values (not raw latest.*): the locals already fall
        // back to the prior cached value when a status response omits a presigned
        // field, so this writes the union rather than the newer half of it.
        updateLibraryItemStatus(userId, id, status, {
          videoUrl,
          sourceText,
          metadata,
          thumbnailUrl,
          vttUrl,
        });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        removeLibraryItem(userId, id);
        return undefined;
      }
      // Swallow errors here; the UI will surface inability to load. Flag the data
      // as stale so the caller knows this status wasn't freshly confirmed.
      statusFresh = false;
    }
  }

  // The timestamped transcript comes from the caption track (see vttCues.ts),
  // not from a separate `segments` field -- that field was permanently empty and
  // has been removed.
  return {
    detail: {
      id: libraryEntry.id,
      title: libraryEntry.title,
      description: libraryEntry.description,
      videoUrl,
      vttUrl,
      status,
      createdAt: libraryEntry.createdAt,
      sourceText,
      metadata,
      thumbnailUrl,
      error,
      estimatedCost,
      estimatedDurationSeconds,
    },
    statusFresh,
  };
}
